<template>
  <div class="workbench-right">
    <!-- 合并后的统一模块：智能追问+AI聊天 -->
    <div class="panel main-container">
      <!-- 顶部标题栏 -->
      <div class="panel-title">
        <div class="title-left">
          <img src="@/assets/casesearchImg/ai-logo.png" class="icons" />
          <span>智能追问</span>
        </div>
        <div class="icon-bk">
          <img src="@/assets/casesearchImg/change.png" class="icons-bk" />
        </div>
      </div>
      <!-- 总结提示气泡 -->
        <div class="panel-label">
          您可以围绕本案事实、证据、法律适用等方面继续提问，我会结合同类裁判案例为您进行深度拆解与专业分析。
        </div>

      <!-- 可滚动区域：绑定ref用于滚动到底 -->
      <div class="scroll-wrap" ref="scrollWrapRef">
        
        <!-- 推荐问题区域 -->
        <div class="recommend-block">
          <div class="sub-title">您可以继续追问</div>
          <div class="question-list">
            <div
              v-for="item in recommendQuestions"
              :key="item.id"
              class="question-item"
              @click="chooseQuestion(item)"
            >
              <span class="text">{{ item.title }}</span>
              <span class="arrow-icon">→</span>
            </div>
          </div>
        </div>

        <!-- 分割线 -->
        <div class="divider"></div>

        <!-- AI聊天记录 -->
        <div class="chat-list">
          <div
            v-for="item in chatList"
            :key="item.id"
            :class="['chat-item', item.role]"
          >
            <div class="avatar">{{ item.role === "user" ? "我" : "AI" }}</div>
            <!-- 用户消息 -->
            <div class="bubble" v-if="item.role === 'user'">{{ item.content }}</div>
            <!-- AI 消息：含思考 + 回答 -->
            <div v-else-if="item.role === 'ai'" class="ai-msg-wrap">
              <!-- 深度思考面板 -->
              <div v-if="item.thinkStreaming || item.thinkContent" class="think-section" :class="{ collapsed: item.thinkCollapsed }">
                <div class="think-header" @click="item.thinkCollapsed = !item.thinkCollapsed">
                  <!-- <span class="think-icon">💡</span> -->
                  <span class="thinking-text">深度思考{{ item.thinkStreaming ? '中' : '完成' }}</span>
                  <span v-if="item.thinkStreaming" class="thinking-dots think-dots-mini"><span></span><span></span><span></span></span>
                  <span v-else>
                    <svg class="think-arrow-svg" :class="{ rotated: !item.thinkCollapsed }" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
                <div v-if="item.thinkContent" class="think-body markdown-body" v-html="marked.parse(fixStreamMarkdown(item.thinkContent))"></div>
                 <!-- <div v-else class="think-body think-body-loading"></div> -->
              </div>
              <!-- 思考中 loading（思考完成折叠后，FollowUp 数据到前） -->
              <div v-if="!item.content && !item.thinkStreaming && item.loadingFollowUp" class="bubble markdown-body loading-bubble">
                <span class="thinking-text">整理中</span>
                <span class="thinking-dots"><span></span><span></span><span></span></span>
              </div>
              <!-- 正式回答 -->
              <div v-if="item.content" class="bubble markdown-body" v-html="marked.parse(fixStreamMarkdown(item.content))"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部固定输入框 -->
      <div class="chat-input">
        <div class="chat-box">
           <a-textarea
          v-model:value="question"
          :rows="3"
          placeholder="基于当前结果继续提问"
          @keydown="handleKeydown"
        />
        <div class="box-end">
            <div>Enter 发送 · Shift+Enter 换行</div>
            <div class="send-arrow" @click="send" :class="{ disabled: isResponding }">
            <img src="@/assets/docImg/icon6.png" class="icons-btn">
          </div>
        </div>
        </div>      
      </div>
    </div>

    <!-- 悬浮弹窗 CaseHoverTip -->
    <div
      v-if="hoverTip.show"
      class="case-hover-tip"
      :style="{ left: hoverTip.x + 'px', top: hoverTip.y + 'px' }"
      @mouseenter="clearHoverTimer"
      @mouseleave="hideHoverTip"
    >
      <div v-if="hoverTip.empty" class="tip-status">暂无该案例详情数据</div>
      <div v-else-if="hoverTip.loading" class="tip-status">加载中...</div>
      <div v-else class="tip-wrap">
        <div class="tip-btn-group">
          <button class="tip-btn detail-btn" @click="goDetail()">
            <span class="btn-icon">◎</span>
            查看详情
          </button>
        </div>
        <h3 class="tip-title">{{ hoverTip.data.title }}</h3>
        <div class="tip-meta">{{ hoverTip.data.meta }}</div>
        <div class="tab-group">
          <span
            v-for="tab in tabOptions"
            :key="tab.key"
            class="tab-item"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </span>
        </div>
        <div class="tip-scroll-content">
          {{ hoverTip.data[getActiveField] }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount, computed, defineExpose } from "vue";
import { message } from 'ant-design-vue'
import { marked } from "marked";
import hljs from 'highlight.js'
import 'highlight.js/styles/atom-one-light.css'
import { SearchByCaseNumber } from "../../api/case/case";
import { getApiBaseUrl } from '@/utils/env'
const apiBaseUrl = getApiBaseUrl() || ''

marked.setOptions({
  gfm: true,
  breaks: true,
  highlight(code, lang) {
    const langLabel = lang || 'text'
    let codeHtml = lang && hljs.getLanguage(lang)
      ? hljs.highlight(code, { language: lang, ignoreIllegals: true }).value
      : hljs.highlightAuto(code).value
    return `<div class="code-wrap">
      <span class="code-lang">${langLabel}</span>
      <button class="code-copy-btn">复制代码</button>
      <pre class="hljs"><code>${codeHtml}</code></pre>
    </div>`
  }
})

/**
 * 检测行是否包含表格单元格（排除代码块）
 */
const isTableLine = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) return false
  return trimmed.includes('|')
}

/**
 * 检测行是否是表格分隔线
 */
const isSeparatorLine = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return false
  let content = trimmed
  if (content.startsWith('|')) content = content.slice(1)
  if (content.endsWith('|')) content = content.slice(0, -1)
  const matches = content.match(/:?-{3,}:?/g)
  return matches !== null && matches.length >= 1
}

/**
 * 解析表格行
 */
const parseTableRow = (line) => {
  const trimmed = line.trim()
  if (!trimmed) return { cells: [], count: 0 }
  let content = trimmed
  const startsWithPipe = content.startsWith('|')
  const endsWithPipe = content.endsWith('|')
  if (startsWithPipe) content = content.slice(1)
  if (endsWithPipe) content = content.slice(0, -1)
  const cells = content.split('|').map(c => c.trim())
  return { cells, count: cells.length, startsWithPipe, endsWithPipe }
}

/**
 * 修复表格行格式
 */
const fixTableRowLine = (cells, colCount) => {
  while (cells.length < colCount) cells.push('')
  if (cells.length > colCount) cells = cells.slice(0, colCount)
  return '| ' + cells.join(' | ') + ' |'
}

/**
 * 流式 Markdown 表格修复：处理分隔线缺 |、列数不齐、缺分隔线、未闭合等问题
 */
const fixStreamMarkdown = (text) => {
  if (!text) return ''
  const lines = text.split('\n')
  const result = []
  let i = 0
  let inCodeBlock = false

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) {
      inCodeBlock = !inCodeBlock
      result.push(line)
      i++
      continue
    }

    if (inCodeBlock) {
      result.push(line)
      i++
      continue
    }

    if (isTableLine(line)) {
      if (result.length > 0 && result[result.length - 1].trim() !== '') {
        result.push('')
      }

      const tableLines = []
      let j = i
      while (j < lines.length) {
        const tl = lines[j]
        const tTrimmed = tl.trim()
        if (tTrimmed.startsWith('```') || tTrimmed.startsWith('~~~')) break
        if (isTableLine(tl)) {
          tableLines.push(tl)
          j++
        } else if (tTrimmed === '') {
          // let hasMore = false
          let k = j + 1
          while (k < lines.length && lines[k].trim() === '') k++
          if (k < lines.length && isTableLine(lines[k])) {
            tableLines.push(tl)
            j++
          } else break
        } else break
      }

      const cleanLines = tableLines.filter(l => l.trim() !== '')
      if (cleanLines.length > 0) {
        let colCount = 0
        cleanLines.forEach(cl => {
          const parsed = parseTableRow(cl)
          colCount = Math.max(colCount, parsed.count)
          if (isSeparatorLine(cl)) {
            const matches = cl.match(/:?-{3,}:?/g)
            if (matches) colCount = Math.max(colCount, matches.length)
          }
        })
        colCount = Math.max(colCount, 1)

        let hasSeparator = false
        cleanLines.forEach(cl => { if (isSeparatorLine(cl)) hasSeparator = true })

        const headerParsed = parseTableRow(cleanLines[0])
        result.push(fixTableRowLine(headerParsed.cells, colCount))

        if (!hasSeparator) {
          result.push('|' + ' :--- |'.repeat(colCount))
        } else {
          result.push('|' + ' :--- |'.repeat(colCount))
        }

        const dataStart = hasSeparator ? 2 : 1
        for (let d = dataStart; d < cleanLines.length; d++) {
          const dp = parseTableRow(cleanLines[d])
          result.push(fixTableRowLine(dp.cells, colCount))
        }

        if (j < lines.length && lines[j].trim() !== '') result.push('')
        i = j
      } else {
        result.push(line)
        i++
      }
    } else {
      result.push(line)
      i++
    }
  }

  if (result.length > 0 && !inCodeBlock) {
    const last = result[result.length - 1]
    const lastTrim = last.trim()
    if (lastTrim.startsWith('|') && !lastTrim.endsWith('|')) {
      for (let k = result.length - 2; k >= 0; k--) {
        const prev = result[k]
        if (isTableLine(prev) && prev.trim().endsWith('|')) {
          const prevCols = (prev.match(/\|/g) || []).length - 1
          const currCols = (last.match(/\|/g) || []).length
          if (prevCols >= currCols) {
            result[result.length - 1] = last + ' |'.repeat(prevCols - currCols + 1)
          }
          break
        }
      }
    }
  }

  return result.join('\n')
}

// const emit = defineEmits(["send"]);
const props = defineProps({
    keyword:{
        type:String,
        default:""
    }
})

// 历史追问记录（用于 API historyQueries 参数）
const historyQueries = ref([]);
// 滚动容器DOM ref
const scrollWrapRef = ref(null);
const question = ref("");
const isResponding = ref(false)

// ====== 案号悬停弹窗逻辑 ======
const emit = defineEmits(['caseClick', 'viewDetail'])
const hoverTip = ref({
  show: false,
  x: 0,
  y: 0,
  caseNo: '',
  empty: false,
  loading: false,
  data: null
})
let hoverDelayTimer = null
const activeTab = ref('aiBrief')
const tabOptions = ref([
  { key: 'aiBrief', label: '案情摘要', field: 'caseSummary' },
  { key: 'courtThink', label: '认定事实', field: 'facts' },
  { key: 'fact', label: '本院认为', field: 'courtOpinion' },
  { key: 'result', label: '裁判结果', field: 'judgment' }
])
const getActiveField = computed(() => {
  const targetTab = tabOptions.value.find(item => item.key === activeTab.value)
  return targetTab ? targetTab.field : 'caseSummary'
})

// 更新 hoverTip 状态
const updateHoverTip = (tipState) => {
  hoverTip.value = { ...hoverTip.value, ...tipState }
}

// 根据案号调用接口获取案例详情
const fetchCaseByAh = async (caseNo) => {
  try {
    const res = await SearchByCaseNumber(caseNo)
    if (res.isSuccess && res.data) {
      const d = res.data
      return {
        title: d.cause || '',
        meta: `${d.court || ''} | 案号：${d.ah || caseNo} | ${d.trialLevel || ''} | ${d.year || ''}年`,
        caseSummary: d.caseSummary || '无内容',
        facts: d.facts || '无内容',
        courtOpinion: d.courtOpinion || '无内容',
        judgment: d.judgment || '无内容'
      }
    }
    return null
  } catch (err) {
    console.error('按案号查询失败', err)
    return null
  }
}

const openHoverTip = async (event, caseNo) => {
  clearHoverTimer()
  hoverDelayTimer = setTimeout(async () => {
    const rect = event.target.getBoundingClientRect()
    const tipWidth = 400
    const margin = 16
    let posX = rect.left - tipWidth - margin
    let posY = rect.top
    if (posX < margin) posX = rect.right + margin
    if (posY < margin) posY = margin

    // 先显示弹窗，展示"加载中..."
    updateHoverTip({
      show: true,
      x: posX,
      y: posY,
      caseNo,
      empty: false,
      loading: true,
      data: null
    })

    // 调用接口获取数据
    const caseInfo = await fetchCaseByAh(caseNo)

    updateHoverTip({
      empty: !caseInfo,
      loading: false,
      data: caseInfo
    })

    activeTab.value = 'aiBrief'
  }, 300)
}

const hideHoverTip = () => {
  clearHoverTimer()
  hoverTip.value = { show: false, x: 0, y: 0, caseNo: '', empty: false, loading: false, data: null }
}

// 点击查看详情
const goDetail = () => {
  const caseNo = hoverTip.value
  console.log(caseNo,'caseNo')
  hideHoverTip()
  emit('viewDetail', caseNo)
}

const clearHoverTimer = () => {
  if (hoverDelayTimer) clearTimeout(hoverDelayTimer)
}

// 不再使用 emitCaseClick，悬停/点击都展示弹窗

const globalDocEvent = async (e) => {
  const target = e.target

  // 只处理自己组件（.workbench-right）内的事件，避免与 MdFileRead 的全局事件冲突
  if (!target.closest('.workbench-right')) return

  // 复制代码按钮
  if (target.classList.contains('code-copy-btn')) {
    const wrap = target.closest('.code-wrap')
    const text = wrap.querySelector('code').innerText
    try {
      await navigator.clipboard.writeText(text)
      target.innerText = '复制成功'
      target.style.color = '#1768ff'
    } catch {
      target.innerText = '复制失败'
    }
    setTimeout(() => {
      target.innerText = '复制代码'
      target.style.color = ''
    }, 2000)
    return
  }
  // 弹窗内点击不处理案号
  if (target.closest('.case-hover-tip')) return
  // 获取案号单元格
  let td = target.closest('td')
  if (!td && e.type === 'click' && hoverTip.value.show) {
    hideHoverTip()
    await new Promise(r => setTimeout(r, 10))
    const elUnder = document.elementFromPoint(e.clientX, e.clientY)
    if (elUnder) td = elUnder.closest('td')
  }
  if (!td) return
  const tr = td.closest('tr')
  if (!tr || td !== tr.children[1]) return
  const caseNo = (td.querySelector('a')?.textContent || td.textContent || '').replace(/\s+/g, '')
  if (!caseNo) return
  if (e.type === 'mouseover') openHoverTip(e, caseNo)
  if (e.type === 'click') openHoverTip(e, caseNo)
}

onMounted(() => {
  document.addEventListener('click', globalDocEvent)
  document.addEventListener('mouseover', globalDocEvent)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', globalDocEvent)
  document.removeEventListener('mouseover', globalDocEvent)
  clearHoverTimer()
})
// ====== 案号悬停弹窗逻辑结束 ======

// 推荐问题数据（由 SearchHome 通过 window.__pendingFollowUpQuestions 或 setRecommendQuestions 设置）
const recommendQuestions = ref([])

// 供父组件直接设置推荐问题
const setRecommendQuestions = (questions) => {
  // console.log(questions,'questions')
  recommendQuestions.value = questions
}

const chatList = ref([
  {
    id: 1,
    role: "ai",
    content: "您可以继续围绕案件事实进行追问，我会结合案例进一步分析。",
  },
]);

// 滚动到最新消息底部方法
const scrollToBottom = async () => {
  await nextTick();
  if (scrollWrapRef.value) {
    scrollWrapRef.value.scrollTop = scrollWrapRef.value.scrollHeight;
  }
};

// 页面初始化后自动滚动到底 + 取用待处理的推荐问题
onMounted(() => {
  scrollToBottom();
  if (window.__pendingFollowUpQuestions) {
    recommendQuestions.value = window.__pendingFollowUpQuestions
    window.__pendingFollowUpQuestions = null
  }
});

const chooseQuestion = (item) => {
  question.value = item.title;
};

const send = async () => {
  if (!question.value.trim()) return;
  if (isResponding.value) {
    message.info('请等待当前回复完成')
    return;
  }
  isResponding.value = true;

  const query = question.value;

  // 新增用户消息
  chatList.value.push({
    id: Date.now(),
    role: "user",
    content: query,
  });

  question.value = "";
  scrollToBottom();

  // 构建 historyQueries：优先用 keyword，再追加历史
  const queries = [];
  if (props.keyword) {
    queries.push(props.keyword);
  }
  historyQueries.value.forEach(q => {
    if (!queries.includes(q)) {
      queries.push(q);
    }
  });

  const token = localStorage.getItem('token');

  // 新增单条 AI 消息（含思考 + 回答）
  const aiMsgId = Date.now() + 1;
  chatList.value.push({
    id: aiMsgId,
    role: "ai",
    content: "",
    thinkContent: "",
    thinkStreaming: true,
    thinkCollapsed: false,
    loadingFollowUp: false,
  });
  const aiIndex = chatList.value.length - 1;
  scrollToBottom();

  // ========== CaseThinkV2 深度思考流式 ==========
  const thinkTask = async () => {
    try {
      const thinkUrl = `${apiBaseUrl}api/DocumentSearch/CaseThinkV2`;
      const thinkResponse = await fetch(thinkUrl, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ query, historyQueries: queries })
      });

      if (thinkResponse.ok && thinkResponse.body) {
        const reader = thinkResponse.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let thinkRunning = true;

        while (thinkRunning) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          while (buffer.includes('\n\n')) {
            const splitIndex = buffer.indexOf('\n\n');
            const fullMsg = buffer.slice(0, splitIndex).trim();
            buffer = buffer.slice(splitIndex + 2);

            if (!fullMsg) continue;

            if (fullMsg === 'data:[DONE]') {
              thinkRunning = false;
              break;
            }

            if (fullMsg.startsWith('data:')) {
              let jsonStr = fullMsg.slice(5).trim();
              if (!jsonStr || !jsonStr.startsWith('{') || !jsonStr.endsWith('}')) {
                continue;
              }

              try {
                const data = JSON.parse(jsonStr);
                if (data.content) {
                  chatList.value[aiIndex] = {
                    ...chatList.value[aiIndex],
                    thinkContent: (chatList.value[aiIndex].thinkContent || '') + data.content,
                    thinkStreaming: true,
                  };
                  scrollToBottom();
                }
              } catch (parseErr) {
                continue;
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('深度思考流式异常：', err);
    }

    // 深度思考完成 → 自动收缩，激活 followUp loading
    if (chatList.value[aiIndex]) {
      chatList.value[aiIndex] = {
        ...chatList.value[aiIndex],
        thinkStreaming: false,
        thinkCollapsed: true,
        loadingFollowUp: true,
      }
    }
  }

  // ========== FollowUp 正式回答 ==========
  // 在 CaseThinkV2 流式数据完全结束后才发起请求
  const followUpTask = async () => {
    const apiUrl = `${apiBaseUrl}api/CaseFollowUp/FollowUp`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ query, historyQueries: queries, })
      });

      if (!response.ok || !response.body) {
        chatList.value[aiIndex] = {
          ...chatList.value[aiIndex],
          content: '接口请求失败',
          loadingFollowUp: false,
        }
        return
      }

      const reader = response.body.getReader();
      const dec = new TextDecoder('utf-8');
      let buffer = '';
      let streamRunning = true;
      let hasReceivedContent = false

      while (streamRunning) {
        const { done, value } = await reader.read();
        if (done) {
          streamRunning = false;
          break;
        }

        buffer += dec.decode(value, { stream: true });

        while (buffer.includes('\n\n')) {
          const splitIndex = buffer.indexOf('\n\n');
          const fullMsg = buffer.slice(0, splitIndex).trim();
          buffer = buffer.slice(splitIndex + 2);

          if (!fullMsg) continue;

          if (fullMsg === 'data:[DONE]') {
            streamRunning = false;
            break;
          }

          if (fullMsg.startsWith('data:')) {
            let jsonStr = fullMsg.slice(5).trim();
            if (!jsonStr || !jsonStr.startsWith('{') || !jsonStr.endsWith('}')) {
              continue;
            }

            try {
              const data = JSON.parse(jsonStr);
              if (data.content) {
                if (!hasReceivedContent) {
                  hasReceivedContent = true
                  chatList.value[aiIndex] = {
                    ...chatList.value[aiIndex],
                    loadingFollowUp: false,
                    content: '',
                  }
                }
                chatList.value[aiIndex] = {
                  ...chatList.value[aiIndex],
                  content: (chatList.value[aiIndex].content || '') + data.content,
                };
                scrollToBottom();
              }
            } catch (parseErr) {
              continue;
            }
          }
        }
      }

      // 流结束了但没内容，关闭 loading
      if (!hasReceivedContent) {
        chatList.value[aiIndex] = {
          ...chatList.value[aiIndex],
          loadingFollowUp: false,
        }
      }
    } catch (err) {
      chatList.value[aiIndex] = {
        ...chatList.value[aiIndex],
        content: '请求失败：请检查网络',
        loadingFollowUp: false,
      }
      console.error('追问流式异常：', err);
    }
  }

  // 先执行深度思考流式，等待完成后再执行 followUp
  await thinkTask();
  await followUpTask();

  // 记录本次 query 到历史
  historyQueries.value.push(query);
  isResponding.value = false;
};

// 重置为初始状态
const reset = () => {
  chatList.value = [
    {
      id: 1,
      role: "ai",
      content: "您可以继续围绕案件事实进行追问，我会结合案例进一步分析。",
    },
  ]
  historyQueries.value = []
  question.value = ''
}

// Enter 发送 Shift+Enter 换行
const handleKeydown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    send()
  }
}

defineExpose({ reset, setRecommendQuestions })
</script>

<style scoped lang="less">
.workbench-right {
  width: 360px;
  // height: 100%;
  position:sticky;
  top: 0;
  right: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.panel.main-container {
  flex: 1;
  min-height: 0;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #E8EDF5;
  display: flex;
  flex-direction: column;
}

// 标题栏
.panel-title {
  font-size: 18px;
  font-weight: 700;
  color: #1F2329;
  margin: 20px 20px 0px  20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .title-left {
    display: flex;
    align-items: center;
  }
}
.icons {
  width: 42px;
  height: 42px;
  margin-right: 8px;
}
.icons-bk {
  width: 16px;
  height: 16px;
}
.icon-bk {
  height: 28px;
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
  box-shadow: 0px 12px 34px 0px rgba(40, 73, 132, 0.08);
  border-radius: 8px;
  border: 1px solid #E1E7F0;
  cursor: pointer;
}

// 核心滚动容器：推荐+聊天全部在这里滚动
.scroll-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  margin: 0 20px 0px  20px;
}

// 顶部总结气泡
.panel-label {
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0px 12px 34px 0px rgba(40, 73, 132, 0.08), 0px 6px 16px 0px rgba(35, 72, 132, 0.04);
  border-radius: 13px;
  border: 1px solid #E5EDF9;
  padding: 16px;
  font-size: 14px;
  color: #52627B;
  line-height: 24px;
  margin: 20px 20px 20px  20px;
}

// 推荐问题模块
.recommend-block {
  margin-bottom: 20px;
}
.sub-title {
  font-size: 16px;
  font-weight: 600;
  color: #1F2329;
  margin-bottom: 14px;
}
.question-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.question-item {
  background: #F6F8FC;
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #31425E;
  line-height: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .text {
    flex: 1;
  }
  .arrow-icon {
    flex-shrink: 0;
    margin-left: 10px;
    color: #86909C;
    font-size: 16px;
  }
}
.question-item:hover {
  background: #EAF2FF;
  color: #1768FF;
  .arrow-icon {
    color: #1768FF;
  }
}

// 分割线
.divider {
  width: 100%;
  height: 1px;
  background: #E8EDF5;
  margin: 24px 0;
}

// 聊天区域
.chat-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.chat-item {
  display: flex;
  max-width: 90%;
}
// AI消息：居左
.chat-item.ai {
  align-self: flex-start;
  flex-direction: row;
}
// AI消息内部包裹（思考面板 + 回答气泡）
.chat-item.ai .ai-msg-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
// 用户消息：居右
.chat-item.user {
  align-self: flex-end;
  flex-direction: row-reverse;
  .avatar {
    margin-left: 10px;
    margin-right: 0;
  }
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #1768FF;
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  margin-right: 10px;
  flex-shrink: 0;
}
.user .avatar {
  background: #1768FF;
}
.bubble {
  flex: 1;
  background: #F6F8FC;
  border-radius: 10px;
  padding: 6px 12px;
  line-height: 24px;
  color: #31425E;
  font-size: 13px;
}
.user .bubble {
  background: #EAF2FF;
}

// 深度思考区域
.think-section {
  background: #f8fafc;
  // border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;

  .think-header {
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    transition: background 0.2s;
    background: #fff;
    // &:hover {
    //   // background: rgba(0,0,0,0.02);
    // }

    .think-icon { font-size: 14px; }
    .think-label {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;
      flex: 1;
    }
    .dot-anim { animation: dotPulse 1.2s infinite; }
      .think-dots-mini span {
        width: 4px !important;
        height: 4px !important;
      }
      .think-arrow-svg {
        color: #6b7a93;
        transition: transform 0.25s ease;
        display: block;
        &.rotated { transform: rotate(90deg); }
      }
  }

  .think-body {
    padding: 0 14px 12px;
    font-size: 12px;
    line-height: 22px;
    color: #475569;
    max-height: 400px;
    overflow-y: auto;
    transition: max-height 0.3s ease;

    &.markdown-body {
      //  background: transparent !important;
       border: none !important;
       margin: 0 !important;
     }
   }

   .think-body-loading {
     display: flex;
     align-items: center;
     gap: 6px;
     padding: 8px 14px 16px;
     font-size: 12px;
     color: #6b7a93;
   }

  &.collapsed .think-body {
    max-height: 0 !important;
    padding: 0 14px;
    overflow: hidden;
  }
}

// loading 气泡
.loading-bubble {
  display: flex;
  align-items: center;
  gap: 6px;
}

@keyframes dotPulse {
  0%, 20% { opacity: 1; }
  40% { opacity: 0.4; }
  60% { opacity: 1; }
  80% { opacity: 0.4; }
  100% { opacity: 1; }
}

// 思考中 loading 动画
.thinking-text {
  font-size: 12px;
  color: #999;
}
.thinking-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 2px;
  span {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #999;
    animation: dot-bounce 1.4s ease-in-out infinite both;
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
    &:nth-child(3) { animation-delay: 0s; }
  }
}
@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

// ========== 最终生效：精简版 Markdown 全局样式 + 整体缩小2-3号字号 ==========
:deep(.markdown-body) {
  background: #fff;
  border-radius: 10px;
  // padding: 18px;
  color: #374151;
  font-size: 12px; // 原14px → 缩小2号
  line-height: 24px;
  border: 1px solid #e5e7eb;
}
:deep(.markdown-body p) { margin: 8px 0; }
:deep(.markdown-body h1,h2,h3,h4) {
  margin: 16px 0 8px;
  font-weight: 600;
  color: #1f2937;
}
:deep(.markdown-body h1) { font-size: 16px; } // 原18
:deep(.markdown-body h2) { 
  position: relative;
  padding-left: 10px;
  font-size: 14px; // 原15
  margin-top: 20px;
}
:deep(.markdown-body h3) { font-size: 13px; }
:deep(.markdown-body h4) { font-size: 12px; }
:deep(.markdown-body h2::before) {
  content: "";
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 3px;
  // background: #1768ff;
  border-radius: 2px;
}
:deep(.markdown-body ul,ol) {
  padding-left: 20px;
  margin: 8px 0;
}
:deep(.markdown-body li) { margin: 4px 0; font-size: 12px; }
:deep(.markdown-body strong) {
  // color: #1768ff;
  font-weight: 600;
  font-size: 12px;
}
:deep(.markdown-body blockquote) {
  border-left: 3px solid #1768ff;
  padding-left: 12px;
  color: #6b7280;
  margin: 10px 0;
  background: #f9fafb;
  padding: 6px 10px;
  border-radius: 0 6px 6px 0;
  font-size: 12px;
}
:deep(.markdown-body :not(pre) > code) {
  background: #eff6ff;
  // color: #1768ff;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 10px; // 原12 缩小2号
}

/* 表格精简+字号缩小 */
:deep(.markdown-body table) {
  width: 100% !important;
  border-collapse: collapse !important;
  margin: 12px 0 !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.markdown-body table th) {
  background: #f9fafb !important;
  padding: 8px 10px !important;
  border: 1px solid #e5e7eb !important;
  font-weight: 600;
  font-size: 11px;
}
:deep(.markdown-body table td) {
  padding: 8px 10px !important;
  border: 1px solid #e5e7eb !important;
  font-size: 11px;
}
:deep(.markdown-body table tr:hover td) {
  background: #f9fafb !important;
}
:deep(.markdown-body table tr th:nth-child(2)),
:deep(.markdown-body table tr td:nth-child(2)) {
  color: #1768ff !important;
  font-weight: 600;
}
:deep(.markdown-body hr) {
  border: none;
  height: 1px;
  background: #e5e7eb;
  margin: 16px 0;
}

/* 代码块简化+字号缩小 */
:deep(.code-wrap) {
  position: relative;
  margin: 12px 0;
}
:deep(.code-lang) {
  position: absolute;
  top: 8px;
  left: 12px;
  font-size: 10px; // 缩小字号
  color: #6b7280;
  z-index: 2;
}
:deep(.code-copy-btn) {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 10px; // 缩小字号
  color: #6b7280;
  background: rgba(255,255,255,.3);
  border: none;
  border-radius: 3px;
  padding: 1px 6px;
  cursor: pointer;
  &:hover {
    color: #1768ff;
    background: rgba(23,104,255,.08);
  }
}
:deep(.markdown-body pre.hljs) {
  padding: 32px 14px 14px;
  border-radius: 8px;
  font-size: 11px; // 原13 缩小2号
  overflow-x: auto;
  margin: 0;
  background: #f9fafb;
}

// 底部输入框固定不滚动
.chat-input {
  flex-shrink: 0;
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  background: rgba(255,255,255,0.96);
  box-shadow: 0px 12px 34px 0px rgba(40,73,132,0.08), 0px -8px 22px 0px rgba(34,66,116,0.04);
  border: 1px solid #E5EDF6;
  gap: 10px;
}
.chat-box{
  background: #FFFFFF;
  box-shadow: 0px 12px 34px 0px rgba(40,73,132,0.08), 0px -8px 22px 0px rgba(34,66,116,0.04);
  border-radius: 12px 12px 12px 12px;
  border: 1px solid #D9E3F1;
  margin: 20px;
  padding: 12px;
}
 :deep(.ant-input) {
    border-color: transparent ;
  }
  .layout-container .ant-input:focus,
  .layout-container .ant-input-focused {
    border-color: transparent !important;
    box-shadow: none !important;
  }
  .content-wrapper .ant-input, .content-wrapper .ant-input-password .ant-input{
    border-color: transparent ;
  }
// antd组件深度样式
:deep(.ant-input-textarea textarea) {
  border-radius: 10px;
  background: #F6F8FC;
  border: 1px solid #E8EDF5;
}
:deep(.ant-btn) {
  height: 42px;
  border-radius: 8px;
  background: #1768FF;
}

// 美化滚动条
.scroll-wrap::-webkit-scrollbar {
  width: 4px;
}
.scroll-wrap::-webkit-scrollbar-thumb {
  background: #D0D8E6;
  border-radius: 4px;
}
.scroll-wrap::-webkit-scrollbar-track {
  background: transparent;
}
.box-end{
  display: flex;
  justify-content: space-between;
  font-weight: 400;
  font-size: 11px;
  color: #8B97A9;
  line-height: 36px;
}
 .send-arrow {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #1677ff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .icons-btn{
  width: 20px;
  height: 20px;
}

/* ====== 案号悬停弹窗样式（与 MdFileRead 一致） ====== */
.case-hover-tip {
  position: fixed;
  z-index: 9999;
  width: 640px;
  max-height: 82vh;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 28px;
  pointer-events: auto;
}
.tip-status {
  padding: 90px 0;
  text-align: center;
  font-size: 15px;
  color: #94a3b8;
}
.tip-wrap {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

/* 按钮组 */
.tip-btn-group {
  display: flex;
  gap: 12px;
}
.tip-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 28px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 14px;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
    transform: translateY(-1px);
  }
  .btn-icon {
    font-size: 17px;
  }
}

.tip-title {
  font-size: 21px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  line-height: 1.45;
}
.tip-meta {
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
}
.tab-group {
  display: flex;
  width: 100%;
  border: none !important;
  border-bottom: none;
}
.tab-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 16px;
  padding: 14px 0;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: color 0.24s ease;
  background: transparent;
  border: none;
  background: transparent;
  border-radius: 0;
}
.tab-item:hover:not(.active) {
  color: #1e293b;
}
.tab-item.active {
  color: #1768ff;
  font-weight: 500;
}
.tab-item.active::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -1px;
  width: 44px;
  height: 3px;
  background: #1768ff;
  border-radius: 2px 2px 0 0;
  transform: translateX(-50%);
}
.tip-scroll-content {
  font-size: 15px;
  line-height: 1.7;
  color: #52627B;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
}
</style>